// SiteHeader — shared top chrome. Active link matches the current route.
//
// Two nav registers:
//   • Wide viewports (>960px): inline horizontal nav, the original layout.
//   • Narrow viewports (≤960px): hamburger button reveals a slide-in
//     drawer from the right with the full nav stacked vertically. Closes
//     on link click, Escape, or backdrop tap. Body scroll is locked while
//     the drawer is open.
//
// The 960px threshold is the point at which the 9 inline links plus the
// right-side cluster (random, journey badge, github) start to crowd. The
// drawer keeps every destination one tap away without horizontal scroll
// or overflow ellipsis.

import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "wouter";
import { useFocusTrap } from "../hooks/useFocusTrap";
import SearchButton from "./SearchButton";
import PathBadge from "./PathBadge";
import RandomPageButton from "./RandomPageButton";
import { LinkMark } from "./icons";

const NAV = [
  { label: "Understand", href: "/learn", children: [
    { label: "Learn", href: "/learn" }, { label: "Trust", href: "/trust" },
    { label: "Language", href: "/language" }, { label: "About", href: "/about" },
  ] },
  { label: "Build", href: "/implement", children: [
    { label: "Implementation", href: "/implement" }, { label: "Specifications", href: "/specs" }, { label: "Governed Autonomy", href: "/governed-autonomy" },
  ] },
  { label: "Evaluate", href: "/research", children: [
    { label: "Research", href: "/research" }, { label: "Canon", href: "/canon" }, { label: "Limits and falsification", href: "/canon/limits" },
  ] },
  { label: "Act", href: "/advocacy", children: [
    { label: "Advocacy", href: "/advocacy" }, { label: "Community", href: "/community" },
  ] },
  { label: "Read", href: "/writing", children: [
    { label: "Writing", href: "/writing" }, { label: "Guided paths", href: "/start" },
  ] },
];

export default function SiteHeader() {
  const [location] = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);

  const isActive = (href: string) => {
    if (href === "/") return location === "/";
    return location.startsWith(href);
  };

  // Auto-close the drawer on route change so a tap on a nav link doesn't
  // leave the menu hanging open over the new page.
  useEffect(() => {
    setMenuOpen(false);
  }, [location]);

  // ESC closes the drawer. Body scroll lock while open so the page
  // underneath doesn't move when the reader scrolls inside the panel.
  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMenuOpen(false);
    };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [menuOpen]);

  return (
    <header
      className="mobile-header-row"
      style={{
        height: "var(--header-height)",
        borderBottom: "1px solid var(--line)",
        display: "flex",
        alignItems: "center",
        padding: "0 24px",
        gap: 32,
        background: "var(--panel)",
        fontFamily: "var(--mono)",
        position: "sticky",
        top: 0,
        zIndex: "var(--z-header)" as unknown as number,
      }}
    >
      <Link
        href="/"
        style={{
          fontSize: 12,
          fontWeight: 600,
          letterSpacing: 1.2,
          color: "var(--ink)",
          display: "flex",
          alignItems: "center",
          gap: 8,
        }}
      >
        <span
          aria-hidden
          style={{
            width: 18,
            height: 18,
            border: "1.5px solid var(--ink)",
            borderRadius: "50%",
            position: "relative",
            display: "inline-block",
          }}
        >
          <span
            style={{
              position: "absolute",
              inset: 4,
              border: "1px solid var(--ink)",
              borderRadius: "50%",
            }}
          />
        </span>
        KTP/
      </Link>

      {/* Persona-driven reading path badge. Renders null when the
          reader hasn't completed the persona engine on /about, so it
          stays invisible by default and lights up only after a
          prescription is written to ktp.path.v1. */}
      <PathBadge />

      <nav
        aria-label="Primary navigation"
        className="site-nav-inline"
        style={{ display: "flex", gap: 22, flex: 1, alignItems: "center" }}
      >
        {NAV.map((item) => {
          const active = item.children.some((child) => child.href !== "/" && isActive(child.href));
          return (
            <Link
              key={item.href}
              href={item.href}
              aria-current={active ? "page" : undefined}
              style={{
                fontSize: 12,
                letterSpacing: 0.5,
                color: active ? "var(--ink)" : "var(--ink-3)",
                borderBottom: active ? "1.5px solid var(--ink)" : "none",
                paddingBottom: 2,
                minHeight: 44,
                display: "inline-flex",
                alignItems: "center",
              }}
            >
              {item.label}
            </Link>
          );
        })}
        <details className="site-sections-menu" key={location} style={{ position: "relative" }} onKeyDown={(event) => {
          if (event.key === "Escape") { event.currentTarget.open = false; event.currentTarget.querySelector("summary")?.focus(); }
        }}>
          <summary style={{ cursor: "pointer", minHeight: 44, display: "flex", alignItems: "center", fontSize: 12, color: "var(--ink-2)" }}>Sections ▾</summary>
          <div style={{ position: "absolute", top: "100%", right: 0, width: 300, maxHeight: "calc(100dvh - 64px)", overflowY: "auto", padding: 16, background: "var(--panel)", border: "1px solid var(--line)", boxShadow: "var(--shadow-lift)" }}>
            {NAV.map((group) => <div key={group.label} style={{ marginBottom: 12 }}>
              <div style={{ color: "var(--accent)", fontSize: 11, fontWeight: 600 }}>{group.label}</div>
              {group.children.map((item) => <Link key={item.href} href={item.href} style={{ display: "flex", alignItems: "center", minHeight: 44, fontSize: 13, color: "var(--ink-2)" }}>{item.label}</Link>)}
            </div>)}
          </div>
        </details>
      </nav>

      {/* Spacer pushes the right-side cluster to the edge when the inline
          nav is hidden by the narrow-viewport breakpoint. */}
      <span className="site-nav-spacer" aria-hidden style={{ flex: 1 }} />

      <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
        <span className="mobile-hide-360">
          <RandomPageButton />
        </span>
        <SearchButton />
        <a
          className="mobile-hide-360"
          href="https://github.com/nmcitra/ktp-rfc"
          target="_blank"
          rel="noreferrer"
          style={{ fontSize: 11, color: "var(--ink-3)" }}
        >
          github<LinkMark />
        </a>
        <button
          type="button"
          className="site-nav-hamburger"
          aria-label={menuOpen ? "Close navigation" : "Open navigation"}
          aria-expanded={menuOpen}
          aria-controls="site-nav-drawer"
          onClick={() => setMenuOpen((v) => !v)}
        >
          <HamburgerIcon open={menuOpen} />
        </button>
      </div>

      <NavDrawer
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        isActive={isActive}
      />
    </header>
  );
}

/* ── Hamburger icon ──────────────────────────────────────────
 *
 * Three lines that morph into an X when open. Pure SVG to match the
 * iconography voice rule (line-only, 1.25px stroke, currentColor). */

function HamburgerIcon({ open }: { open: boolean }) {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 28 28"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      aria-hidden
    >
      <line
        x1="5"
        y1="9"
        x2="23"
        y2="9"
        style={{
          transformOrigin: "14px 9px",
          transform: open
            ? "translate(0,5px) rotate(45deg)"
            : "translate(0,0) rotate(0)",
          transition: "transform 220ms var(--ease-out-soft)",
        }}
      />
      <line
        x1="5"
        y1="14"
        x2="23"
        y2="14"
        style={{
          opacity: open ? 0 : 1,
          transition: "opacity 160ms var(--ease-out-soft)",
        }}
      />
      <line
        x1="5"
        y1="19"
        x2="23"
        y2="19"
        style={{
          transformOrigin: "14px 19px",
          transform: open
            ? "translate(0,-5px) rotate(-45deg)"
            : "translate(0,0) rotate(0)",
          transition: "transform 220ms var(--ease-out-soft)",
        }}
      />
    </svg>
  );
}

/* ── NavDrawer ──────────────────────────────────────────
 *
 * Slide-in panel from the right with the full nav list. Always rendered
 * so the slide animation works on first open; visibility/transform
 * gated by `open`. The backdrop swallows clicks to close. */

function NavDrawer({
  open,
  onClose,
  isActive,
}: {
  open: boolean;
  onClose: () => void;
  isActive: (href: string) => boolean;
}) {
  // Focus trap while open: Tab cycles inside the drawer, focus lands on
  // the close button first, and returns to the hamburger on close.
  // Escape is already handled by SiteHeader's keydown effect.
  const drawerRef = useRef<HTMLElement>(null);
  useFocusTrap(drawerRef, open);

  return (
    <>
      {/* Backdrop. pointer-events follows visibility so the inline page
          stays interactive when the drawer is closed. */}
      <div
        onClick={onClose}
        aria-hidden
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(20, 20, 18, 0.45)",
          opacity: open ? 1 : 0,
          pointerEvents: open ? "auto" : "none",
          transition: "opacity 220ms var(--ease-out-soft)",
          zIndex: 90,
        }}
      />
      <aside
        ref={drawerRef}
        id="site-nav-drawer"
        role="dialog"
        aria-modal="true"
        aria-label="Site navigation"
        aria-hidden={open ? undefined : true}
        style={{
          position: "fixed",
          top: 0,
          right: 0,
          height: "100dvh",
          width: "min(320px, 86vw)",
          background: "var(--panel)",
          borderLeft: "1px solid var(--line)",
          boxShadow: open ? "-12px 0 40px rgba(0,0,0,0.18)" : "none",
          transform: open ? "translateX(0)" : "translateX(100%)",
          // visibility flips at the end of the slide-out (and instantly on
          // slide-in), keeping the closed drawer's links out of the tab
          // order without breaking the animation.
          visibility: open ? "visible" : "hidden",
          transition:
            "transform 280ms var(--ease-out-soft), box-shadow 280ms var(--ease-out-soft), visibility 280ms",
          display: "flex",
          flexDirection: "column",
          zIndex: 100,
          overflowY: "auto",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "16px 20px",
            borderBottom: "1px solid var(--line)",
            fontFamily: "var(--mono)",
            fontSize: 10,
            letterSpacing: 0.8,
            textTransform: "uppercase",
            color: "var(--ink-3)",
          }}
        >
          <span>navigate</span>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close navigation"
            style={{
              background: "transparent",
              border: 0,
              padding: 4,
              minWidth: 44,
              minHeight: 44,
              cursor: "pointer",
              color: "var(--ink-2)",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <HamburgerIcon open={true} />
          </button>
        </div>
        <nav
          style={{
            display: "flex",
            flexDirection: "column",
            padding: "8px 0",
          }}
        >
          {NAV.map((group) => <div key={group.label} style={{ padding: "8px 22px" }}>
            <div style={{ fontFamily: "var(--mono)", color: "var(--accent)", fontSize: 11, fontWeight: 600, letterSpacing: 0.5 }}>{group.label}</div>
            {group.children.map((item) => <Link key={item.href} href={item.href} onClick={onClose} aria-current={isActive(item.href) ? "page" : undefined} style={{ display: "flex", alignItems: "center", minHeight: 44, padding: "8px 0", color: isActive(item.href) ? "var(--accent)" : "var(--ink-2)", fontSize: 14 }}>{item.label}</Link>)}
          </div>)}
        </nav>
        <div
          style={{
            marginTop: "auto",
            padding: "16px 22px",
            borderTop: "1px solid var(--line)",
            display: "flex",
            flexDirection: "column",
            gap: 10,
            fontFamily: "var(--mono)",
            fontSize: 11,
            color: "var(--ink-3)",
            letterSpacing: 0.4,
          }}
        >
          <a
            href="https://github.com/nmcitra/ktp-rfc"
            target="_blank"
            rel="noreferrer"
            onClick={onClose}
            style={{ color: "var(--ink-2)", textDecoration: "none" }}
          >
            github<LinkMark />
          </a>
          <Link
            href="/colophon"
            onClick={onClose}
            style={{ color: "var(--ink-3)", textDecoration: "none" }}
          >
            colophon
          </Link>
        </div>
      </aside>
    </>
  );
}
